// components/button/SecureMuiSubmitButton.tsx
// MUI Button 기반의 SecureSubmitButton
import type { ButtonProps } from '@mui/material';
import { forwardRef, useRef } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { Button, CircularProgress } from '@mui/material';

type SecureMuiSubmitButtonProps = ButtonProps & {
  isPending: boolean; // 비동기 작업 진행중 여부
  preventDoubleClick?: boolean; // 추가 이중 클릭 방지 (선택)
  throttleMs?: number; // 이중 클릭 방지 시간
  showLoadingIcon?: boolean; // 로딩 아이콘 표시 여부
};

export const SecureMuiSubmitButton = forwardRef<HTMLButtonElement, SecureMuiSubmitButtonProps>(
  (
    {
      isPending,
      preventDoubleClick = true,
      throttleMs = 500,
      showLoadingIcon = true,
      onClick,
      children,
      className,
      ...props
    },
    ref
  ) => {
    const theme = useTheme();
    const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isClickBlocked = useRef(false);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      // 1차 방어: React Query isPending 체크
      if (isPending) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // 2차 방어: 이중 클릭 방지 (선택적)
      if (preventDoubleClick && isClickBlocked.current) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // 클릭 블록 활성화
      if (preventDoubleClick) {
        isClickBlocked.current = true;

        // 이전 타임아웃 정리
        if (clickTimeoutRef.current) {
          clearTimeout(clickTimeoutRef.current);
        }

        // throttleMs 후 클릭 블록 해제
        clickTimeoutRef.current = setTimeout(() => {
          isClickBlocked.current = false;
        }, throttleMs);
      }

      // 실제 클릭 핸들러 실행
      onClick?.(e);
    };

    const isLoading = isPending && showLoadingIcon;

    return (
      <Button
        ref={ref}
        className={className}
        {...props}
        disabled={props.disabled || isPending}
        onClick={handleClick}
      >
        <ContentWrapper>
          {isLoading && (
            <LoadingOverlay>
              <CircularProgress size={20} sx={{ color: theme.colors['ic-white'] }} />
            </LoadingOverlay>
          )}
          <ChildrenWrapper $isLoading={isLoading}>{children}</ChildrenWrapper>
        </ContentWrapper>
      </Button>
    );
  }
);

// 버튼 크기 유지를 위한 wrapper
const ContentWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

// 로딩 아이콘을 중앙에 absolute로 배치
const LoadingOverlay = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

// children 공간은 유지하되 로딩 중에는 숨김
const ChildrenWrapper = styled.span<{ $isLoading: boolean }>`
  visibility: ${({ $isLoading }) => ($isLoading ? 'hidden' : 'visible')};
`;
